import { useEffect, useRef, useState } from 'react';
import type { GhostTrack, MapSnapshot, PublicActor } from '@smysly/shared';
import { WorldEngine } from './WorldEngine.js';
import { onWorldEffect } from './events.js';
import { sound } from '../lib/sound.js';
import { haptic } from '../lib/telegram.js';

export function WorldCanvas({
  snapshot,
  actors,
  ghostTracks,
  onMeaningTap,
  onMove,
}: {
  snapshot: MapSnapshot;
  actors: PublicActor[];
  ghostTracks: GhostTrack[];
  onMeaningTap(id: string): void;
  onMove(x: number, y: number): void;
}): React.JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const engine = useRef<WorldEngine | null>(null);
  const snapshotRef = useRef(snapshot);
  const [trace, setTrace] = useState<string | null>(null);
  snapshotRef.current = snapshot;

  useEffect(() => {
    if (!host.current) return;
    const world = new WorldEngine({
      onMeaningTap: (id) => {
        sound.play('bubble_touch');
        haptic('touch', snapshotRef.current.preferences.haptics);
        onMeaningTap(id);
      },
      onActorTap: (actor) => {
        setTrace(actor.kind === 'ghost'
          ? `Чей-то след. ${actor.meanings.some((meaning) => meaning.publicCategory) ? `В его карте появлялись: ${actor.meanings.map((meaning) => meaning.publicCategory).filter(Boolean).join(' · ')}` : 'Этот человек проходил здесь раньше.'}`
          : 'Цветной путник исследует свои смыслы рядом. Возможность познакомиться появится на следующем этапе.');
        window.setTimeout(() => setTrace(null), 4600);
      },
      onMove,
      onSpawn: () => {
        sound.play('bubble_spawn');
        haptic('create', snapshotRef.current.preferences.haptics);
      },
    });
    engine.current = world;
    void world.init(host.current).then(() => {
      world.syncMeanings(snapshotRef.current.meanings);
      world.syncActors(actors);
      world.syncGhostTracks(ghostTracks);
    });
    const unsubscribe = onWorldEffect((effect) => {
      world.effect(effect);
      if (effect.type === 'selection') sound.play('journey_select');
    });
    return () => { unsubscribe(); world.destroy(); engine.current = null; };
  }, []);

  useEffect(() => { engine.current?.syncMeanings(snapshot.meanings); }, [snapshot.meanings]);
  useEffect(() => { engine.current?.syncActors(actors); }, [actors]);
  useEffect(() => { engine.current?.syncGhostTracks(ghostTracks); }, [ghostTracks]);

  return (
    <div className="world-canvas" ref={host}>
      {trace && <div className="trace-toast" role="status">{trace}</div>}
      <div className="move-hint" aria-hidden="true">коснитесь пространства, чтобы двигаться</div>
    </div>
  );
}
